import { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { Reveal } from "./Reveal";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Globe, ArrowUpRight, Loader2 } from "lucide-react";

const EMAILJS_SERVICE_ID = "service_sik7ybh";
const EMAILJS_TEMPLATE_ID = "template_f4qbuno";
const EMAILJS_PUBLIC_KEY = "y-WbxDI0gIBOYgkQu";

const SERVICE_OPTIONS = [
  "Intermediación Crediticia y SOFOMES",
  "Gestión de Fondos y Sociedades de Inversión",
  "Dispersión de Dinero y Administración de Pensiones",
  "Financiamiento Popular y Rural",
  "Compañías Afianzadoras y Autofinanciamiento",
  "Otro / Consulta general",
];

export const Contacto = () => {
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Por favor, completa los campos obligatorios.");
      return;
    }
    setLoading(true);
    try {
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        formRef.current,
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      toast.success(
        "Mensaje enviado. Te contactaremos a la brevedad."
      );
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      console.error("EmailJS error:", err);
      toast.error(
        "No fue posible enviar el mensaje. Inténtalo de nuevo o escríbenos directamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contacto-section"
      className="relative bg-[#F7F6F1] py-24 md:py-36"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-10 md:gap-16">
          {/* Left: info */}
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <div className="flex items-center gap-3 text-[#017766] mb-6">
                <span className="h-px w-10 bg-[#017766]" />
                <span className="overline">Contacto</span>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-4xl md:text-6xl leading-[1.02] text-[#17351D] mb-8">
                Comencemos su <em className="text-[#017766]">proceso</em>.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-base md:text-lg text-[#17351D]/65 leading-relaxed mb-12 max-w-md">
                Compártenos los detalles de su caso. Un asesor especializado le
                contactará para evaluar la mejor solución.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <ul className="space-y-7">
                <Item
                  icon={MapPin}
                  k="Dirección"
                  v="Calle Topografos 635, int. 2, Arcos de Guadalupe, C.P. 45037, Zapopan, Jalisco, México."
                />
                <Item icon={Phone} k="Teléfono" v="33 1 279 7811" href="tel:+523312797811" />
                <Item
                  icon={Mail}
                  k="Email"
                  v="contacto@lexbalanceperfecto.com"
                  href="mailto:contacto@lexbalanceperfecto.com"
                />
                <Item icon={Globe} k="Web" v="lexbalanceperfecto.com" />
              </ul>
            </Reveal>
          </div>

          {/* Right: form */}
          <div className="col-span-12 lg:col-span-7 lg:pl-10">
            <Reveal delay={0.05}>
              <form
                ref={formRef}
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="bg-white p-8 md:p-12 border border-[#17351D]/8"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
                  <Field
                    label="Nombre completo *"
                    name="name"
                    value={form.name}
                    onChange={handle("name")}
                    placeholder="Ej. María González"
                    testid="form-input-name"
                    required
                  />
                  <Field
                    label="Correo electrónico *"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handle("email")}
                    placeholder="ejemplo@correo.com"
                    testid="form-input-email"
                    required
                  />
                  <Field
                    label="Teléfono"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handle("phone")}
                    placeholder="+52 33 0000 0000"
                    testid="form-input-phone"
                  />
                  <div>
                    <label className="overline text-[#017766] block mb-3">
                      Servicio de interés
                    </label>
                    <select
                      name="service"
                      data-testid="form-input-service"
                      value={form.service}
                      onChange={handle("service")}
                      className="input-underline appearance-none cursor-pointer"
                    >
                      <option value="">Seleccionar...</option>
                      {SERVICE_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-7">
                  <label className="overline text-[#017766] block mb-3">
                    Mensaje *
                  </label>
                  <textarea
                    name="message"
                    data-testid="form-input-message"
                    value={form.message}
                    onChange={handle("message")}
                    placeholder="Cuéntanos brevemente sobre tu caso o solicitud..."
                    rows={4}
                    required
                    className="input-underline"
                  />
                </div>

                <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                  <p className="text-xs text-[#17351D]/55 max-w-md leading-relaxed">
                    Al enviar este formulario, acepta ser contactado por un
                    miembro de Lex Balance Perfecto. La información compartida
                    se trata con estricta confidencialidad.
                  </p>
                  <button
                    type="submit"
                    data-testid="form-submit-btn"
                    disabled={loading}
                    className="btn-primary bg-[#17351D] text-white font-semibold text-[12px] tracking-[0.22em] uppercase px-8 py-5 inline-flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin" size={16} />
                        Enviando...
                      </>
                    ) : (
                      <>
                        Enviar mensaje
                        <ArrowUpRight size={16} strokeWidth={2.2} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, type = "text", placeholder, required, testid }) => (
  <div>
    <label className="overline text-[#017766] block mb-3">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      data-testid={testid}
      className="input-underline"
    />
  </div>
);

const Item = ({ icon: Icon, k, v, href }) => {
  const content = (
    <>
      <div className="w-11 h-11 border border-[#17351D]/15 flex items-center justify-center shrink-0 text-[#17351D]">
        <Icon size={18} strokeWidth={1.6} />
      </div>
      <div>
        <div className="overline text-[#017766] mb-1">{k}</div>
        <div className="font-display text-[17px] text-[#17351D] leading-snug">{v}</div>
      </div>
    </>
  );
  return (
    <li>
      {href ? (
        <a href={href} className="flex items-start gap-5 hover:text-[#017766] transition-colors">
          {content}
        </a>
      ) : (
        <div className="flex items-start gap-5">{content}</div>
      )}
    </li>
  );
};

export default Contacto;
